import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, take } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { CartolaService } from 'src/app/dashboard/services/cartola.service';
import Swal from 'sweetalert2';

/**
 * Guard que protege rutas de outflow CLP únicamente cuando la línea de
 * crédito del usuario está bloqueada.
 *
 * Modelo: el saldo (positivo, cero o negativo) no es razón de bloqueo a
 * nivel de routing — esa validación queda a cargo de las calculadoras
 * (que ya validan monto vs saldoActual / saldoContable según corresponda)
 * y del backend. Lo único que impide al usuario llegar a la pantalla
 * operativa es que su LC esté bloqueada, porque en ese caso ninguna
 * operación va a poder concretarse y el operador necesita intervenir.
 *
 * Lógica:
 *   - Usuario VE (tipoId === 'VE'): pasa siempre (su flow no toca balances CLP)
 *   - Usuario CL con LC no bloqueada: pasa
 *   - Usuario CL con LC bloqueada: bloquea → contactar a soporte
 *
 * El backend (NestJS + lambdas) sigue rechazando outflows que excedan el
 * cupo según la regla waterfall. Este guard solo evita la pantalla en
 * el caso terminal donde nada puede ejecutarse.
 */
@Injectable({
  providedIn: 'root',
})
export class OverdraftGuard implements CanActivate {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private cartolaService: CartolaService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const auth: any = await firstValueFrom(
      this.store.select('auth').pipe(take(1)),
    );
    const user = auth?.user;

    if (!user?.usuarioId) {
      return true;
    }

    // Usuarios VE no usan el flow de sobregiro CLP.
    if (user.tipoId === 'VE') {
      return true;
    }

    try {
      const { lineaBloqueada } = await this.cartolaService.getSaldoCliente(
        user.usuarioId,
      );

      // Único bloqueo: línea de crédito bloqueada. Cualquier otro estado
      // de saldo (positivo, cero o negativo) deja pasar — la validación
      // fina del monto la hacen las calculadoras y el backend.
      if (!lineaBloqueada) {
        return true;
      }

      await Swal.fire({
        icon: 'warning',
        title: 'No puedes operar en este momento',
        text: 'Tu línea de crédito está bloqueada. Contacta a soporte para regularizar.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',
        showCancelButton: true,
        cancelButtonText: 'Volver',
      }).then(() => {
        this.router.navigate(['/wallet/home']);
      });

      return false;
    } catch (error) {
      // Fail-open: si el endpoint de saldo falla, permitir continuar.
      // El backend va a rechazar igual si la regla no se cumple.
      console.warn(
        '[OverdraftGuard] Error obteniendo saldo, permitiendo continuar:',
        error,
      );
      return true;
    }
  }
}
